import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { PX_TO_REM, SIZES } from '../../fundamentals/sizes/sizes';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { Button, ButtonSize } from '../Button/Button';
import { BUTTON_VARIANTS } from '../Button/buttonVariants';
import { MATERIAL_ICONS, MATERIAL_ICONS_TYPES } from '../Icon/icons';
import { IconButton } from '../IconButton/IconButton';
import { BaseDiv } from '../../common/BaseElements';

export interface IChatIconProps {
  text?: string;
  variant?: ChatIconVariant;
  vertical?: boolean;
  peek?: boolean;
  onChatStart?: () => void;
}

export type ChatIconVariant = typeof BUTTON_VARIANTS.PRIMARY | typeof BUTTON_VARIANTS.HERO | typeof BUTTON_VARIANTS.WHITE;

const Container = styled(BaseDiv)<Pick<IChatIconProps, 'vertical'> & { expanded?: boolean }>`
  width: fit-content;
  bottom: ${PX_TO_REM[24]};
  right: ${PX_TO_REM[24]};
  position: fixed;
  ${({ vertical, expanded }) =>
    vertical &&
    expanded &&
    css`
      transform: rotate(-90deg) translate(100%, 0);
      transform-origin: right bottom;
    `};
`;

export const ChatIcon: React.FC<IChatIconProps> = ({ variant = BUTTON_VARIANTS.PRIMARY, text, vertical = false, peek = true, onChatStart, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'ChatIcon');
  const [expanded, setExpanded] = useState(peek && !!text);

  const sharedProps = {
    size: SIZES.LARGE as ButtonSize,
    variant,
    onClick: () => onChatStart?.()
  };

  const handleExpanding = (expand: boolean) => !peek && !!text && setExpanded(expand);

  return (
    <Container
      {...generateTestId()}
      vertical={vertical}
      {...rest}
      onMouseLeave={() => handleExpanding(false)}
      onMouseEnter={() => handleExpanding(true)}
      expanded={expanded}
    >
      {expanded ? (
        <Button {...sharedProps} endIconType={MATERIAL_ICONS_TYPES.OUTLINE} endIcon={MATERIAL_ICONS.QuestionAnswer} {...generateTestId('TextButton')}>
          {text}
        </Button>
      ) : (
        <IconButton {...sharedProps} iconType={MATERIAL_ICONS_TYPES.OUTLINE} icon={MATERIAL_ICONS.QuestionAnswer} {...generateTestId('IconButton')} />
      )}
    </Container>
  );
};
